import { Modal, Typography, Button, Divider, Row, Col } from "antd";
import React, { useEffect, useState } from "react";
import { 
  CheckCircleFilled, 
  CrownOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  HomeOutlined,
  CloseOutlined
} from "@ant-design/icons";

const { Text, Title } = Typography;

const SuccessPage = ({ 
  isSuccessModalOpen, 
  onCloseSuccessModal, 
  subscriptionDetails,
  planName = "Premium",
  amount = "₹5,999",
  validUntil,
  transactionId,
  paymentMethod = "Razorpay"
}) => {
  const [countdown, setCountdown] = useState(5);
  const isWalletSuccess = planName === "Wallet";

  useEffect(() => {
    if (isSuccessModalOpen) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onCloseSuccessModal();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(timer);
        setCountdown(5);
      };
    }
  }, [isSuccessModalOpen, onCloseSuccessModal]);

  const handleBack = () => {
    if (onCloseSuccessModal) onCloseSuccessModal();
  };

  // Format date if provided
  const formattedDate = validUntil 
    ? new Date(validUntil).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : null;

  // Generate transaction ID if not provided
  const displayTransactionId = transactionId || `TXN${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

  const isMobile = window.innerWidth <= 768;

  return (
    <Modal
      open={isSuccessModalOpen}
      onCancel={handleBack}
      footer={null}
      closable={false}
      width={isMobile ? "95%" : 500}
      centered
      maskClosable={false}
      maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      bodyStyle={{
        padding: 0,
        borderRadius: '20px',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      {/* Simple Header */}
      <div style={{
        background: '#ffffff',
        padding: isMobile ? '24px 20px 16px' : '28px 24px 16px',
        textAlign: 'center',
        borderBottom: '1px solid #f0f0f0'
      }}>
        {/* Success Icon */}
        <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: '#e6f7ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <CheckCircleFilled style={{
            fontSize: 40,
            color: '#40d13d',
          }} />
        </div>

        <Title level={3} style={{
          margin: 0,
          fontSize: isMobile ? 20 : 22,
          fontWeight: 600,
          color: '#1a3353',
        }}>
          Payment Successful!
        </Title>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          marginTop: 4
        }}>
          {/* <CrownOutlined style={{ color: '#faad14', fontSize: 16 }} /> */}
          <Text style={{ color: '#666', fontSize: 14 }}>
            {isWalletSuccess ? "Wallet Recharged Successfully" : `${planName} Plan Activated`}
          </Text>
          {/* <CrownOutlined style={{ color: '#faad14', fontSize: 16 }} /> */}
        </div>
      </div>

      {/* Content Section */}
      <div style={{ padding: isMobile ? '20px' : '24px' }}>
        {/* Amount Display */}
        <div style={{
          textAlign: 'center',
          marginBottom: 20
        }}>
          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
            Amount Paid
          </Text>
          <Title level={2} style={{
            margin: 0,
            fontSize: isMobile ? 32 : 36,
            fontWeight: 700,
            color: '#1890ff',
            lineHeight: 1.2
          }}>
            {amount}
          </Title>
        </div>

        <Divider style={{ margin: '16px 0', borderColor: '#f0f0f0' }} />

        {/* Transaction Details - Simple Row/Col Layout */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {/* <Col span={12}>
            <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Transaction ID
              </Text>
              <Text style={{ fontSize: 12, color: '#1a3353', fontWeight: 500, wordBreak: 'break-all' }}>
                {displayTransactionId.slice(0, 12)}...
              </Text>
            </div>
          </Col> */}
          <Col span={12}>
            <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Payment Method
              </Text>
              <Text style={{ fontSize: 12, color: '#1a3353', fontWeight: 500 }}>
                <CreditCardOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                {paymentMethod}
              </Text>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                {isWalletSuccess ? "Recharge Type" : "Plan Type"}
              </Text>
              <Text style={{ fontSize: 12, color: '#1a3353', fontWeight: 500 }}>
                {isWalletSuccess ? "Wallet Recharge" : `${planName} (Yearly)`}
              </Text>
            </div>
          </Col>
          {formattedDate && (
            <Col span={12}>
              <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                  Valid Until
                </Text>
                <Text style={{ fontSize: 12, color: '#1a3353', fontWeight: 500 }}>
                  <CalendarOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                  {formattedDate}
                </Text>
              </div>
            </Col>
          )}
        </Row>

        {/* Features Preview - Simple List */}
        {!isWalletSuccess && (
          <div style={{ 
            background: '#fafafa', 
            borderRadius: '12px',
            padding: '16px',
            marginBottom: 20
          }}>
            <Text style={{ fontSize: 14, fontWeight: 600, color: '#1a3353', display: 'block', marginBottom: 12 }}>
              What's included:
            </Text>
            <Row gutter={[12, 8]}>
              {[
                'View company profiles',
                'Unlimited searches',
                'Priority support',
                'Advanced filters',
                'Download contacts',
                'Premium access'
              ].map((feature, index) => (
                <Col span={12} key={index}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleFilled style={{ color: '#52c41a', fontSize: 12 }} />
                    <Text style={{ fontSize: 12, color: '#4a5a72' }}>{feature}</Text>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Main Action Button */}
        <Button
          type="primary"
          size="large"
          onClick={handleBack}
          style={{
            width: '100%',
            height: 44,
            borderRadius: '8px',
            fontSize: 15,
            fontWeight: 500,
            background: '#1890ff',
            border: 'none',
            marginBottom: 16
          }}
        >
          Go to Dashboard
        </Button>

        {/* Auto-close Timer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8
        }}>
          <div style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: '2px solid #1890ff',
            borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite'
          }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Auto-closing in {countdown} seconds
          </Text>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};

export default SuccessPage;
